import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import dayjs from 'dayjs';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  DATE_TIME_FORMAT_CHANGED_EVENT,
  DATE_TIME_FORMAT_LOCAL_STORAGE_KEY,
} from '@/shared/features/header/components/date-time-format/date-time-format.constants';
import {
  TFormatUpdatedTimestampOptions,
  TTrackedTimestamp,
  TUseDateUtilsReturnType,
} from '@/shared/hooks/use-date-utils/use-date-utils.types';
import {
  formatDateWithOrder,
  formatTimestampByPeriod,
  getDateOrder,
  getNextTickMs,
} from '@/shared/hooks/use-date-utils/use-date-utils.utils';
import { BillingPeriodEnum } from '@/shared/types/types.generated';
import {
  getDefaultDateTimeFormat,
  normalizeTimestamp,
} from '@/shared/utils/format';

export const useDateUtils = (): TUseDateUtilsReturnType => {
  const { t, i18n } = useTranslation();
  const [dateTimeFormat, setDateTimeFormat] = useState<string>(
    getDefaultDateTimeFormat(),
  );
  const [tick, setTick] = useState(0);

  // Accumulates timestamps pushed during render; flushed to state post-commit
  const pendingRef = useRef<TTrackedTimestamp[]>([]);
  const [trackedTimestamps, setTrackedTimestamps] = useState<
    TTrackedTimestamp[]
  >([]);

  // Listen for format changes from other hook instances
  useEffect(() => {
    const handleFormatChange = () => {
      setDateTimeFormat(getDefaultDateTimeFormat());
    };

    // Listen for custom event (same tab)
    window.addEventListener(DATE_TIME_FORMAT_CHANGED_EVENT, handleFormatChange);

    // Listen for storage changes (cross-tab sync)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === DATE_TIME_FORMAT_LOCAL_STORAGE_KEY) {
        setDateTimeFormat(getDefaultDateTimeFormat());
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener(
        DATE_TIME_FORMAT_CHANGED_EVENT,
        handleFormatChange,
      );
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Commit the render-accumulated list to state after each committed render.
  // useLayoutEffect is safe here: runs post-commit, so no risk of concurrent discard.
  useLayoutEffect(() => {
    const pending = pendingRef.current;
    pendingRef.current = [];
    setTrackedTimestamps((prev) => {
      if (
        prev.length === pending.length &&
        prev.every((p, i) => p.timestamp === pending[i].timestamp)
      ) {
        return prev;
      }
      return pending;
    });
  });

  // Re-run whenever the tracked set changes OR after each tick so the next
  // boundary is always scheduled without being reset by unrelated re-renders.
  useEffect(() => {
    if (trackedTimestamps.length === 0) return;

    const pendingBoundariesMs = trackedTimestamps
      .map(({ timestamp, options }) => getNextTickMs(timestamp, options))
      .filter((boundaryMs): boundaryMs is number => boundaryMs !== null);

    if (pendingBoundariesMs.length === 0) return;

    const soonestBoundaryMs = Math.min(...pendingBoundariesMs);
    const timeoutId = setTimeout(
      () => setTick((currentTick) => currentTick + 1),
      soonestBoundaryMs,
    );
    return () => clearTimeout(timeoutId);
  }, [trackedTimestamps, tick]);

  const formatUpdatedTimestamp = (
    timestamp: string | number,
    options: TFormatUpdatedTimestampOptions = {
      ignoreThresholds: {
        minute: false,
        hour: false,
        day: false,
      },
    },
  ): string => {
    pendingRef.current.push({ timestamp, options });

    const now = Date.now();
    const diffMs = now - new Date(normalizeTimestamp(timestamp)).getTime();

    const thresholds = {
      minute: 60 * 1000,
      hour: 60 * 60 * 1000,
      day: 24 * 60 * 60 * 1000,
    };

    if (diffMs < thresholds.minute && !options?.ignoreThresholds?.minute) {
      return t('ui:format_updated_timestamp.just_now');
    }
    if (diffMs < thresholds.hour && !options?.ignoreThresholds?.hour) {
      const minutes = Math.floor(diffMs / (60 * 1000));
      return t('ui:format_updated_timestamp.minutes_ago', { count: minutes });
    }
    if (diffMs < thresholds.day && !options?.ignoreThresholds?.day) {
      const hours = Math.floor(diffMs / (60 * 60 * 1000));
      return t('ui:format_updated_timestamp.hours_ago', { count: hours });
    }

    return formatLocalTime(timestamp);
  };

  const formatLocalTime = (
    utcTimestamp?: string | number,
    timeZone?: string,
    customFormat?: string,
  ): string => {
    if (!utcTimestamp) {
      return '-';
    }

    try {
      const date = parseISO(normalizeTimestamp(utcTimestamp));
      const userTimezone = dayjs.tz.guess();
      const zonedDate = toZonedTime(date, timeZone ?? userTimezone);

      return format(zonedDate, customFormat ?? dateTimeFormat);
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Invalid date';
    }
  };

  const _setDateTimeFormat = (format: string) => {
    setDateTimeFormat(format);
    localStorage.setItem(DATE_TIME_FORMAT_LOCAL_STORAGE_KEY, format);
    window.dispatchEvent(new CustomEvent(DATE_TIME_FORMAT_CHANGED_EVENT));
  };

  const formatByPeriod = useCallback(
    (
      timestamp: string,
      period: BillingPeriodEnum | undefined,
      sinceLabel: (date: string) => string,
    ) =>
      formatTimestampByPeriod(
        timestamp,
        i18n.language,
        period,
        sinceLabel,
        dateTimeFormat,
      ),
    [dateTimeFormat, i18n.language],
  );

  const formatOrderedDate = useCallback(
    (date: Date) =>
      formatDateWithOrder(date, i18n.language, getDateOrder(dateTimeFormat)),
    [dateTimeFormat, i18n.language],
  );

  return {
    formatLocalTime,
    formatUpdatedTimestamp,
    formatByPeriod,
    formatOrderedDate,
    dateTimeFormat,
    setDateTimeFormat: _setDateTimeFormat,
  };
};
